import type { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import RenderReactPdfViewer from '@/components/react-pdf-viewer/render-react-pdf-viewer'
import HTMLBaseRenderer from '@/content-viewer/components/base-renderers/HTMLBaseRenderer'
import ContentViewerErrorState from '@/content-viewer/components/common/ContentViewerErrorState'
import ContentViewerLoadingSpinner from '@/content-viewer/components/common/ContentViewerLoadingStates'
import { RenderFormat } from '@/content-viewer/store/schemas'
import { useUserFileViewableContent } from '../hooks/useUserFileViewableContent'

type UserFileContentRendererProps = {
  userFileId: string
  format: RenderFormat.PDF | RenderFormat.MARKDOWN
  title?: string
  pdfHighlights: PaxtonHighlightArea[]
  markdownHighlights: string[]
  startingPage?: number
}

export default function UserFileContentRenderer(props: UserFileContentRendererProps): JSX.Element {
  const { userFileId, format, title, pdfHighlights, markdownHighlights, startingPage } = props
  const { loading, error, pdfFile, htmlString, retry } = useUserFileViewableContent({
    userFileId,
    format,
    title,
    markdownHighlights,
  })

  if (loading) {
    return <ContentViewerLoadingSpinner size="small" />
  }

  if (error) {
    return <ContentViewerErrorState message={format === RenderFormat.PDF ? 'Failed to load PDF file.' : 'Failed to load markdown content.'} onRetry={retry} />
  }

  if (format === RenderFormat.PDF) {
    if (!pdfFile) {
      return <ContentViewerErrorState message="Failed to load PDF file." onRetry={retry} />
    }
    return <RenderReactPdfViewer pdfFile={pdfFile} highlightAreas={pdfHighlights} startingPage={startingPage} />
  }

  if (!htmlString) {
    return <ContentViewerErrorState message="Failed to load markdown content." onRetry={retry} />
  }
  return <HTMLBaseRenderer htmlContent={htmlString} highlightSentences={markdownHighlights} />
}
